// frontend/src/components/Requisitions/RequisitionWizard.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import apiClient from "../../api/apiClient";
import RequisitionForm from "./RequisitionForm";
import RequisitionItems from "./RequisitionItems";
import RequisitionQuotesPanel from "./RequisitionQuotesPanel";

/**
 * ✅ Normalizar payloads paginados / no paginados
 */
const getResultsFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return { results: payload, next: null };
  }
  if (payload && Array.isArray(payload.results)) {
    return {
      results: payload.results,
      next: payload.next || null,
    };
  }
  return { results: [], next: null };
};

/**
 * ✅ Fetch paginado genérico
 */
async function fetchAllPages(url) {
  let nextUrl = url;
  const rows = [];

  while (nextUrl) {
    const { data } = await apiClient.get(nextUrl);
    const { results, next } = getResultsFromPayload(data);
    rows.push(...results);
    nextUrl = next;
  }

  return rows;
}

/**
 * ✅ Wrapper: maneja 409 de duplicados (CREATE/UPDATE)
 * - interactive=false: NO muestra confirm (para autosave), solo regresa { cancelled:true, needsConfirmation:true }
 * - interactive=true: muestra confirm y si el usuario acepta reintenta con force_duplicates=1
 * - forceRequest=true: manda la request inicial ya con force_duplicates=1
 */
async function saveWithDuplicateCheck({
  reqId = null,
  payload,
  method = "put",
  interactive = true,
  actionLabel = "GUARDAR / CREAR",
  forceRequest = false,
}) {
  const baseUrl = method === "post" ? `/requisitions/` : `/requisitions/${reqId}/`;
  const withForce = (url) => `${url}${url.includes("?") ? "&" : "?"}force_duplicates=1`;
  const requestUrl = forceRequest ? withForce(baseUrl) : baseUrl;

  try {
    const res =
      method === "post"
        ? await apiClient.post(requestUrl, payload)
        : method === "patch"
          ? await apiClient.patch(requestUrl, payload)
          : await apiClient.put(requestUrl, payload);

    return { ok: true, data: res.data, forced: !!forceRequest };
  } catch (err) {
    const httpStatus = err?.response?.status;
    const data = err?.response?.data;

    if (httpStatus === 409 && Array.isArray(data?.duplicates) && data.duplicates.length > 0) {
      if (!interactive) {
        return {
          ok: false,
          cancelled: true,
          needsConfirmation: true,
          duplicates: data.duplicates,
          window_days: data.window_days,
        };
      }

      const lines = data.duplicates.slice(0, 8).map((d) => {
        const when = d.date ? new Date(d.date).toLocaleString("es-MX") : "";
        const pct = Number(d.match_ratio ?? 0) * 100;
        const count = Number(d.match_count ?? 0);
        return `• #${d.id} — ${when} — match ${pct.toFixed(0)}% (${count} item(s))`;
      });

      const msg =
        `⚠️ Posible duplicado detectado (ventana ${data.window_days} días).\n\n` +
        lines.join("\n") +
        `\n\n¿Quieres ${actionLabel} de todos modos?`;

      const userAccepted = window.confirm(msg);

      if (!userAccepted) {
        return { ok: false, cancelled: true, duplicates: data.duplicates };
      }

      const forceUrl = withForce(baseUrl);

      const res2 =
        method === "post"
          ? await apiClient.post(forceUrl, payload)
          : method === "patch"
            ? await apiClient.patch(forceUrl, payload)
            : await apiClient.put(forceUrl, payload);

      return { ok: true, data: res2.data, forced: true, duplicates: data.duplicates };
    }

    throw err;
  }
}

// ✅ Helpers (Fase 5: lógica por FK; texto solo display)
const toPositiveIntOrNull = (v) => {
  if (v === null || typeof v === "undefined" || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const getUserFullName = (u) => ((u?.first_name || "") + " " + (u?.last_name || "")).trim();

// ✅ Mejora de robustez: department_display -> department_ref(code/name) -> department legacy
const getUserDepartmentDisplay = (u) => {
  const display = (u?.department_display || "").toString().trim();
  if (display) return display;

  const code = (u?.department_ref?.code || "").toString().trim();
  const name = (u?.department_ref?.name || "").toString().trim();
  if (code && name) return `${code} - ${name}`;
  if (code || name) return code || name;

  return (u?.department || "").toString().trim();
};

const buildInitialFormData = (u) => ({
  department: getUserDepartmentDisplay(u),

  department_ref_id: toPositiveIntOrNull(u?.department_ref_id) ?? "",

  administrative_unit_ref:
    toPositiveIntOrNull(u?.administrative_unit_id) ??
    toPositiveIntOrNull(u?.administrative_unit?.id) ??
    "",

  project: "",
  funding_source: "",
  budget_unit: "",
  agreement: "",
  tender: "",
  title: "",
  description: "",
  external_service: "",

  fecha: new Date().toLocaleDateString("es-MX"),
  solicitante: getUserFullName(u),

  project_label: "",
  funding_source_label: "",
  budget_unit_label: "",
  agreement_label: "",
  tender_label: "",
  external_service_label: "",
});

export default function RequisitionWizard() {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  // ----- Step 1 data -----
  const [formData, setFormData] = useState(() => buildInitialFormData(user));

  useEffect(() => {
    const userDeptDisplay = getUserDepartmentDisplay(user);
    const userDeptFk = toPositiveIntOrNull(user?.department_ref_id);
    const userAdministrativeUnitFk =
      toPositiveIntOrNull(user?.administrative_unit_id) ??
      toPositiveIntOrNull(user?.administrative_unit?.id);

    setFormData((prev) => ({
      ...prev,
      department: userDeptDisplay || prev.department,
      department_ref_id: userDeptFk ?? prev.department_ref_id,
      administrative_unit_ref: userAdministrativeUnitFk ?? prev.administrative_unit_ref,
      solicitante: getUserFullName(user) || prev.solicitante,
    }));
  }, [user]);

  const step1FormRef = useRef(null);

  // ====== Step 2 shared state ======
  const [items, setItems] = useState([]);
  const [requisitionNumber, setRequisitionNumber] = useState("");

  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(null);

  // ✅ ACK costo aproximado realista
  const [ackCostRealistic, setAckCostRealistic] = useState(false);

  // ✅ Draft requisition
  const [draftReq, setDraftReq] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);

  // ✅ Bloqueo si hay PDF seleccionado sin partidas marcadas
  const [quoteDraftInvalid, setQuoteDraftInvalid] = useState(false);

  // ✅ itemsRef para no trabajar con estado stale
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // ✅ modo manual (tender = NO APLICA)
  const manualItemsMode = useMemo(() => {
    const s = String(formData.tender_label || formData.tender || "").trim();
    if (!s) return false;
    return /NO\s*APLICA/i.test(s);
  }, [formData.tender_label, formData.tender]);

  // ✅ Mostrar cotizaciones + checklist SOLO cuando Licitación = NO APLICA
  const showQuotesAndAck = manualItemsMode;

  // ✅ Bloqueo del botón Guardar SOLO para NO APLICA sin checkbox
  const disableSaveByAck = showQuotesAndAck && !ackCostRealistic;

  useEffect(() => {
    if (!showQuotesAndAck) {
      if (quoteDraftInvalid) setQuoteDraftInvalid(false);
      if (ackCostRealistic) setAckCostRealistic(false);
    }
  }, [showQuotesAndAck, quoteDraftInvalid, ackCostRealistic]);

  // --- Fetch Departments (fallback temporal para compatibilidad) ---
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchAllPages("/catalogs/departments/");
        setDepartments(rows);
      } catch (err) {
        console.error("Error loading departments", err);
      }
    })();
  }, []);

  /**
   * ✅ Fase 5: resolver departamento solicitante priorizando FK
   * 1) formData.department_ref_id
   * 2) user.department_ref_id
   * 3) fallback temporal por texto/catálogo
   */
  const resolveRequestingDepartmentId = () => {
    const fromFormFk = toPositiveIntOrNull(formData.department_ref_id);
    if (fromFormFk) return fromFormFk;

    const fromUserFk = toPositiveIntOrNull(user?.department_ref_id);
    if (fromUserFk) return fromUserFk;

    const departmentText = String(formData.department || "").trim();
    if (!departmentText) return null;

    const textNorm = departmentText.toLowerCase();

    const d = departments.find((dep) => {
      const depName = String(dep?.name || "").trim();
      const depCode = String(dep?.code || "").trim();

      if (!depName && !depCode) return false;

      const nameNorm = depName.toLowerCase();
      const codeNorm = depCode.toLowerCase();

      return (
        (depName && departmentText === depName) ||
        (depCode && departmentText === depCode) ||
        (depName && textNorm.includes(nameNorm)) ||
        (depCode && textNorm.includes(codeNorm))
      );
    });

    return d ? d.id : null;
  };

  /**
   * ✅ Unidad Administrativa requerida por backend
   */
  const resolveAdministrativeUnitId = () => {
    const fromFormFk = toPositiveIntOrNull(formData.administrative_unit_ref);
    if (fromFormFk) return fromFormFk;

    const fromUserFk =
      toPositiveIntOrNull(user?.administrative_unit_id) ??
      toPositiveIntOrNull(user?.administrative_unit?.id);

    if (fromUserFk) return fromUserFk;

    return null;
  };

  const handleCancel = async () => {
    if (draftReq?.id) {
      const ok = window.confirm("¿Cancelar y descartar la requisición en borrador?");
      if (!ok) return;

      try {
        await apiClient.delete(`/requisitions/${draftReq.id}/`);
      } catch (e) {
        console.warn("No se pudo borrar borrador:", e?.response?.status || e?.message);
      }
    } else {
      const ok = window.confirm("¿Cancelar?");
      if (!ok) return;
    }
    navigate("/requisitions");
  };

  const handleResetStep1 = async () => {
    if (draftReq?.id) {
      const ok = window.confirm("Esto borrará el borrador en backend. ¿Continuar?");
      if (!ok) return;

      try {
        await apiClient.delete(`/requisitions/${draftReq.id}/`);
      } catch (e) {
        console.warn("No se pudo borrar borrador:", e?.response?.status || e?.message);
      }
    }

    setDraftReq(null);
    setFormData(buildInitialFormData(user));
    setItems([]);
    setRequisitionNumber("");
    setAckCostRealistic(false);
    setQuoteDraftInvalid(false);
    setStep(1);
  };

  const handleNextFromStep1 = () => {
    const ok = step1FormRef.current?.reportValidity();
    if (!ok) return;

    const deptId = resolveRequestingDepartmentId();
    if (!deptId) {
      showToast(
        "No se pudo determinar el Departamento Solicitante (catálogo). Revisa el campo.",
        "error"
      );
      return;
    }

    const administrativeUnitId = resolveAdministrativeUnitId();
    if (!administrativeUnitId) {
      showToast(
        "No se pudo determinar la Unidad Administrativa del usuario. Revisa /users/me/ o la sesión.",
        "error"
      );
      return;
    }

    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  // ===== Helpers =====
  const idOrUndef = (v) =>
    v === "" || v === null || typeof v === "undefined" ? undefined : Number(v);

  const strOrUndef = (v) => {
    const s = (v ?? "").toString().trim();
    return s.length ? s : undefined;
  };

  const compact = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => typeof v !== "undefined"));

  const parseMoney = (v) => {
    if (v === null || typeof v === "undefined") return NaN;
    if (typeof v === "number") return v;
    const s = String(v).trim();
    if (!s) return NaN;
    const cleaned = s.replace(/\$/g, "").replace(/\s+/g, "").replace(/,/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  const hasRealItemId = (it) =>
    Number.isInteger(Number(it?.id)) &&
    Number(it.id) > 0 &&
    !it?.isTemp;

  const buildHeaderPayload = ({ finalize = false } = {}) => {
    const deptId = resolveRequestingDepartmentId();
    const administrativeUnitId = resolveAdministrativeUnitId();

    if (!deptId || !administrativeUnitId) return null;

    const base = {
      requesting_department: deptId,
      project: idOrUndef(formData.project),
      funding_source: idOrUndef(formData.funding_source),
      budget_unit: idOrUndef(formData.budget_unit),
      agreement: idOrUndef(formData.agreement),
      tender: idOrUndef(formData.tender),
      external_service: idOrUndef(formData.external_service),
      administrative_unit_ref: administrativeUnitId,
      requisition_reason: strOrUndef(formData.description),
      ack_cost_realistic: !!ackCostRealistic,
      status: finalize ? "registered" : undefined,
    };

    return compact(base);
  };

  const normalizeItemsForPayload = (itemsList) => {
    const invalidLines = [];

    const normalizedItems = (itemsList || []).map((it, idx) => {
      const lineNo = idx + 1;

      const product = Number(it.product);
      const quantity = Number(it.quantity);
      const unit = Number(it.unit);
      const description = Number(it.description);

      const manualText = String(it.manual_description ?? "").trim();
      const isManual =
        manualText.length > 0 ||
        it.description === null ||
        it.description === "" ||
        typeof it.description === "undefined";

      let estimatedTotal = parseMoney(it.estimated_total);
      const unitCost = parseMoney(it.estimated_unit_cost);

      if (!Number.isFinite(estimatedTotal) || estimatedTotal <= 0) {
        if (
          Number.isFinite(unitCost) &&
          unitCost > 0 &&
          Number.isFinite(quantity) &&
          quantity > 0
        ) {
          estimatedTotal = Number((unitCost * quantity).toFixed(2));
        }
      }

      if (!Number.isFinite(product) || product <= 0) invalidLines.push(lineNo);
      if (!Number.isFinite(quantity) || quantity <= 0) invalidLines.push(lineNo);
      if (!Number.isFinite(unit) || unit <= 0) invalidLines.push(lineNo);
      if (!Number.isFinite(unitCost) || unitCost <= 0) invalidLines.push(lineNo);

      if (isManual) {
        if (!manualText) invalidLines.push(lineNo);
      } else {
        if (!Number.isFinite(description) || description <= 0) invalidLines.push(lineNo);
      }

      if (!Number.isFinite(estimatedTotal) || estimatedTotal <= 0) invalidLines.push(lineNo);

      const payloadItem = {
        ...(hasRealItemId(it) ? { id: Number(it.id) } : {}),
        product,
        quantity,
        unit,
        estimated_unit_cost: Number(unitCost.toFixed(2)),
        estimated_total: Number(estimatedTotal.toFixed(2)),
      };

      if (isManual) {
        payloadItem.description = null;
        payloadItem.manual_description = manualText;
      } else {
        payloadItem.description = description;
        payloadItem.manual_description = null;
      }

      return payloadItem;
    });

    const uniqInvalid = Array.from(new Set(invalidLines)).sort((a, b) => a - b);

    return { invalidLines: uniqInvalid, normalizedItems };
  };

  const mergeItemIds = (localItems, serverItems) => {
    const to2 = (v) => {
      const n = parseMoney(v);
      return Number.isFinite(n) ? Number(n).toFixed(2) : "";
    };

    const normNull = (v) => (v === null || typeof v === "undefined" ? "" : String(v));
    const normText = (v) => String(v ?? "").trim().toLowerCase();

    const keyOf = (it) =>
      [
        normNull(it.product),
        normNull(it.unit),
        normNull(Number(it.quantity ?? "") || ""),
        to2(it.estimated_unit_cost),
        to2(it.estimated_total),
        normNull(it.description),
        normText(it.manual_description ?? it.description_text ?? it.description_label ?? ""),
      ].join("|");

    const buckets = new Map();
    (serverItems || []).forEach((s) => {
      const k = keyOf(s);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(s);
    });

    return (localItems || []).map((l) => {
      const k = keyOf(l);
      const arr = buckets.get(k);
      if (arr && arr.length) {
        const picked = arr.shift();
        const { isTemp, ...rest } = l;
        return { ...rest, id: picked.id };
      }
      return l;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ evitar doble POST por race condition
  // ─────────────────────────────────────────────────────────────────────────
  const draftReqRef = useRef(null);
  useEffect(() => {
    draftReqRef.current = draftReq;
  }, [draftReq]);

  const draftCreatePromiseRef = useRef(null);

  /**
   * Crea el borrador con POST /requisitions/
   * El backend lo deja en draft.
   */
  async function createDraftWithItems(normalizedItems, { interactiveDuplicates = true } = {}) {
    if (draftReqRef.current?.id) return { ok: true, data: draftReqRef.current, forced: false };

    if (draftCreatePromiseRef.current) {
      return await draftCreatePromiseRef.current;
    }

    const header = buildHeaderPayload({ finalize: false });
    if (!header) return { ok: false, error: "HEADER" };

    if (!Array.isArray(normalizedItems) || normalizedItems.length === 0) {
      return { ok: false, error: "NO_ITEMS" };
    }

    setDraftBusy(true);

    draftCreatePromiseRef.current = (async () => {
      const result = await saveWithDuplicateCheck({
        payload: { ...header, items: normalizedItems },
        method: "post",
        interactive: interactiveDuplicates,
        actionLabel: "CREAR",
      });

      if (!result.ok && result.cancelled) {
        return {
          ok: false,
          cancelled: true,
          needsConfirmation: !!result.needsConfirmation,
          duplicates: result.duplicates,
        };
      }

      const created = result.data;

      draftReqRef.current = created;
      setDraftReq(created);
      setRequisitionNumber((prev) => prev || String(created?.id || ""));

      if (Array.isArray(created?.items)) {
        setItems((prev) => mergeItemIds(prev, created.items));
      }

      return { ok: true, data: created, forced: !!result.forced, duplicates: result.duplicates };
    })()
      .catch((e) => {
        console.error("No se pudo crear requisición:", e);
        throw e;
      })
      .finally(() => {
        draftCreatePromiseRef.current = null;
        setDraftBusy(false);
      });

    return await draftCreatePromiseRef.current;
  }

  /**
   * ✅ Guardar borrador existente con PUT /requisitions/{id}/
   * - finalize=false => sigue en draft
   * - finalize=true  => manda status=registered
   */
  async function saveExistingDraftWithItems({
    interactiveDuplicates = true,
    silent = true,
    finalize = false,
    forceDuplicates = false,
  } = {}) {
    const draftId = draftReqRef.current?.id;
    if (!draftId) return { ok: false, error: "NO_DRAFT" };

    const header = buildHeaderPayload({ finalize });
    if (!header) return { ok: false, error: "HEADER" };

    const currentItems = itemsRef.current || [];
    if (!currentItems.length) return { ok: false, error: "NO_ITEMS" };

    const { invalidLines, normalizedItems } = normalizeItemsForPayload(currentItems);
    if (invalidLines.length) {
      return { ok: false, error: "INVALID_LINES", invalidLines };
    }

    setDraftBusy(true);
    try {
      const payload = {
        ...header,
        items: normalizedItems,
      };

      const result = await saveWithDuplicateCheck({
        reqId: draftId,
        payload,
        method: "put",
        interactive: interactiveDuplicates,
        actionLabel: finalize ? "FINALIZAR" : "GUARDAR",
        forceRequest: forceDuplicates,
      });

      if (!result.ok && result.cancelled) {
        if (!silent && !result.needsConfirmation) {
          showToast("Guardado cancelado.", "error");
        }

        return {
          ok: false,
          cancelled: true,
          needsConfirmation: !!result.needsConfirmation,
          duplicates: result.duplicates,
        };
      }

      const updated = result.data;

      draftReqRef.current = updated;
      setDraftReq(updated);
      setRequisitionNumber((prev) => prev || String(updated?.id || ""));

      const merged = Array.isArray(updated?.items)
        ? mergeItemIds(currentItems, updated.items)
        : currentItems;

      setItems(merged);

      if (!silent) {
        const extra = result.forced
          ? " (Confirmaste posible duplicado)"
          : " (Sin duplicados detectados)";
        showToast(`OK.${extra}`, "success");
      }

      return {
        ok: true,
        data: updated,
        items: merged,
        forced: !!result.forced,
        duplicates: result.duplicates,
      };
    } catch (e) {
      console.error("No se pudo guardar el borrador existente con items:", e);
      if (!silent) showToast("No se pudo guardar.", "error");
      return { ok: false, error: "PUT_FAILED", detail: e };
    } finally {
      setDraftBusy(false);
    }
  }

  /**
   * ✅ Guardar centralizado
   * - finalize=false => autosave / guardado intermedio
   * - finalize=true  => deja la requisición en registered
   */
  async function doSaveAll({
    silent = true,
    interactiveDuplicates = true,
    finalize = false,
  } = {}) {
    const header = buildHeaderPayload({ finalize: false });
    if (!header) return { ok: false, error: "HEADER" };

    const currentItems = itemsRef.current || [];
    if (!currentItems || currentItems.length === 0) {
      return { ok: false, error: "NO_ITEMS" };
    }

    const { invalidLines, normalizedItems } = normalizeItemsForPayload(currentItems);
    if (invalidLines.length) return { ok: false, error: "INVALID_LINES", invalidLines };

    // 1) Si no existe requisición aún → POST con items (queda en draft)
    if (!draftReqRef.current?.id) {
      const createdRes = await createDraftWithItems(normalizedItems, { interactiveDuplicates });
      if (!createdRes.ok) {
        return { ...createdRes, ok: false, error: createdRes.error || "CREATE_BLOCKED" };
      }

      // Si solo era guardado intermedio, aquí termina
      if (!finalize) {
        return {
          ok: true,
          data: createdRes.data,
          forced: !!createdRes.forced,
          duplicates: createdRes.duplicates,
        };
      }

      // Si sí era finalización, hacemos un segundo PUT con status=registered
      const finalizeRes = await saveExistingDraftWithItems({
        interactiveDuplicates,
        silent,
        finalize: true,
        forceDuplicates: !!createdRes.forced,
      });

      if (!finalizeRes.ok) return finalizeRes;

      return {
        ok: true,
        data: finalizeRes.data,
        forced: !!(createdRes.forced || finalizeRes.forced),
        duplicates: finalizeRes.duplicates || createdRes.duplicates,
      };
    }

    // 2) Si ya existe borrador → guardar TODO por PUT
    return await saveExistingDraftWithItems({
      interactiveDuplicates,
      silent,
      finalize,
    });
  }

  // ✅ Autosave: NO debe sacar confirm; si hay 409 solo avisa una vez
  const autosaveTimerRef = useRef(null);
  const lastSignatureRef = useRef("");
  const dupToastShownRef = useRef(false);

  const autosaveSignature = useMemo(() => {
    if (step !== 2) return "";

    const header = buildHeaderPayload({ finalize: false });
    if (!header) return "";

    if (!items || items.length === 0) return "";

    const { invalidLines, normalizedItems } = normalizeItemsForPayload(items);
    if (invalidLines.length) return "INVALID";

    const stripIds = (arr) => (arr || []).map(({ id, ...rest }) => rest);

    return JSON.stringify({
      header,
      items: stripIds(normalizedItems),
      ack: !!ackCostRealistic,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, items, ackCostRealistic, formData, departments, user]);

  useEffect(() => {
    dupToastShownRef.current = false;
  }, [autosaveSignature]);

  useEffect(() => {
    if (step !== 2) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    if (!autosaveSignature || autosaveSignature === "INVALID") return;

    const header = buildHeaderPayload({ finalize: false });
    if (!header) return;

    if (autosaveSignature === lastSignatureRef.current) return;

    autosaveTimerRef.current = setTimeout(async () => {
      lastSignatureRef.current = autosaveSignature;

      const r = await doSaveAll({
        silent: true,
        interactiveDuplicates: false,
        finalize: false,
      });

      if (!r.ok && r.needsConfirmation && !dupToastShownRef.current) {
        dupToastShownRef.current = true;
        showToast("⚠️ Posible duplicado detectado. Al guardar se te pedirá confirmación.", "error");
      }

      if (!r.ok && !r.needsConfirmation) {
        lastSignatureRef.current = "";
      }
    }, 500);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveSignature, step]);

  // ====== Finalizar (Guardar) ======
  const savingRef = useRef(false);

  const handleFinishFromStep2 = async () => {
    if (savingRef.current) return;

    if (quoteDraftInvalid) {
      showToast(
        "Tienes una cotización seleccionada pero sin partidas marcadas. Completa los checkboxes o quita el PDF.",
        "error"
      );
      return;
    }

    if (showQuotesAndAck && !ackCostRealistic) {
      showToast('Debes confirmar "costo aproximado pero realista" para poder guardar.', "error");
      return;
    }

    const header = buildHeaderPayload({ finalize: false });
    if (!header) {
      showToast(
        "No se pudo determinar el Departamento Solicitante o la Unidad Administrativa.",
        "error"
      );
      return;
    }

    if (!items || items.length === 0) {
      showToast("Agrega al menos una partida antes de continuar.", "error");
      return;
    }

    const { invalidLines } = normalizeItemsForPayload(items);
    if (invalidLines.length) {
      showToast(`Falta información/total válido en los renglones: ${invalidLines.join(", ")}`, "error");
      return;
    }

    try {
      savingRef.current = true;
      setSaving(true);
      setSavingMode("finalize");

      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

      const res = await doSaveAll({
        silent: true,
        interactiveDuplicates: true,
        finalize: true,
      });

      const saved = res?.data;
      const id = saved?.id || draftReqRef.current?.id;

      if (!id) {
        showToast("No se pudo guardar. Intenta de nuevo.", "error");
        return;
      }

      if (String(saved?.status || "").trim().toLowerCase() !== "registered") {
        showToast("La requisición se guardó, pero no se pudo finalizar correctamente.", "error");
        return;
      }

      const effectiveNum = requisitionNumber || String(id);
      const dupMsg = res?.forced
        ? "⚠️ Guardada bajo confirmación de posible duplicado."
        : "✅ No se detectaron duplicados en la ventana configurada.";

      showToast(`Requisición #${effectiveNum} guardada correctamente. ${dupMsg}`, "success");
      navigate("/requisitions");
    } catch (e) {
      console.error(e);
      showToast("Error al finalizar requisición.", "error");
    } finally {
      setSaving(false);
      setSavingMode(null);
      savingRef.current = false;
    }
  };

  const handleSaveDraftFromStep2 = async () => {
    if (savingRef.current) return;

    if (quoteDraftInvalid) {
      showToast(
        "Tienes una cotización seleccionada pero sin partidas marcadas. Completa los checkboxes o quita el PDF.",
        "error"
      );
      return;
    }

    const header = buildHeaderPayload({ finalize: false });
    if (!header) {
      showToast(
        "No se pudo determinar el Departamento Solicitante o la Unidad Administrativa.",
        "error"
      );
      return;
    }

    if (!items || items.length === 0) {
      showToast("Agrega al menos una partida antes de guardar el borrador.", "error");
      return;
    }

    const { invalidLines } = normalizeItemsForPayload(items);
    if (invalidLines.length) {
      showToast(
        `Falta información/total válido en los renglones: ${invalidLines.join(", ")}`,
        "error"
      );
      return;
    }

    try {
      savingRef.current = true;
      setSaving(true);
      setSavingMode("draft");

      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

      const res = await doSaveAll({
        silent: true,
        interactiveDuplicates: true,
        finalize: false,
      });

      const saved = res?.data;
      const id = saved?.id || draftReqRef.current?.id;

      if (!id) {
        showToast("No se pudo guardar el borrador. Intenta de nuevo.", "error");
        return;
      }

      const effectiveNum = requisitionNumber || String(id);
      const dupMsg = res?.forced
        ? " Confirmaste un posible duplicado."
        : "";

      showToast(`Borrador #${effectiveNum} guardado correctamente.${dupMsg}`, "success");
      navigate("/requisitions");
    } catch (e) {
      console.error(e);
      showToast("Error al guardar borrador.", "error");
    } finally {
      setSaving(false);
      setSavingMode(null);
      savingRef.current = false;
    }
  };

  const allItemsHaveId = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.every((it) => hasRealItemId(it));
  }, [items]);

  return (
    <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow">
      <h1 className="text-2xl font-bold mb-4">Registro de Requisición</h1>

      {/* STEP 1 */}
      {step === 1 && (
        <>
          <form ref={step1FormRef} onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-4">
              <RequisitionForm embed formData={formData} setFormData={setFormData} />
            </div>
          </form>

          <div className="flex justify-between mt-6 gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleResetStep1}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
            >
              Borrar
            </button>
            <button
              type="button"
              onClick={handleNextFromStep1}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Siguiente
            </button>
          </div>
        </>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <>
          <RequisitionItems
            formData={formData}
            items={items}
            setItems={setItems}
            requisitionNumber={requisitionNumber}
            setRequisitionNumber={setRequisitionNumber}
            manualItemsMode={manualItemsMode}
            ackCostRealistic={ackCostRealistic}
            setAckCostRealistic={setAckCostRealistic}
            showAckCostRealistic={showQuotesAndAck}
          />

          {/* ✅ Panel Cotizaciones solo para NO APLICA */}
          {showQuotesAndAck && (
            items.length === 0 ? (
              <div className="mt-6 border rounded p-4 bg-slate-50 text-sm text-gray-700">
                Agrega al menos una partida para habilitar cotizaciones.
              </div>
            ) : !draftReq?.id ? (
              <div className="mt-6 border rounded p-4 bg-slate-50 text-sm text-gray-700">
                Guardando borrador para habilitar cotizaciones (creando requisición e IDs)…
              </div>
            ) : !allItemsHaveId ? (
              <div className="mt-6 border rounded p-4 bg-slate-50 text-sm text-gray-700">
                Guardando borrador para habilitar cotizaciones (asignando IDs a partidas)…
              </div>
            ) : (
              <RequisitionQuotesPanel
                requisitionId={draftReq.id}
                items={items}
                onDraftInvalidChange={setQuoteDraftInvalid}
              />
            )
          )}

          {/* Footer actions */}
          <div className="flex justify-end mt-6 gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded"
              disabled={saving || draftBusy}
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleBackToStep1}
              className="bg-slate-500 hover:bg-slate-600 text-white px-4 py-2 rounded"
              disabled={saving || draftBusy}
            >
              Regresar
            </button>

            <button
              type="button"
              onClick={handleSaveDraftFromStep2}
              className={`bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded disabled:opacity-60 ${
                quoteDraftInvalid ? "opacity-60" : ""
              }`}
              disabled={saving || quoteDraftInvalid || draftBusy}
              title={
                quoteDraftInvalid
                  ? "Hay una cotización seleccionada sin partidas marcadas (completa o quita el PDF)."
                  : draftBusy
                    ? "Guardando borrador..."
                    : ""
              }
            >
              {savingMode === "draft" || draftBusy
                ? "Guardando borrador..."
                : quoteDraftInvalid
                  ? "Guardar Borrador (cotización incompleta)"
                  : "Guardar Borrador"}
            </button>

            <button
              type="button"
              onClick={handleFinishFromStep2}
              className={`bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:opacity-60 ${
                disableSaveByAck || quoteDraftInvalid ? "opacity-60" : ""
              }`}
              disabled={saving || disableSaveByAck || quoteDraftInvalid || draftBusy}
              title={
                quoteDraftInvalid
                  ? "Hay una cotización seleccionada sin partidas marcadas (completa o quita el PDF)."
                  : disableSaveByAck
                    ? 'Debes confirmar "costo aproximado pero realista" para poder guardar.'
                    : draftBusy
                      ? "Guardando borrador..."
                      : ""
              }
            >
              {savingMode === "finalize" || (saving && savingMode !== "draft")
                ? "Guardando..."
                : draftBusy
                  ? "Guardando borrador..."
                : quoteDraftInvalid
                  ? "Guardar (cotización incompleta)"
                  : disableSaveByAck
                    ? "Guardar (confirma costo)"
                    : "Guardar"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
